/**
 * Transcript Viewer — line-by-line rendering of deposition/hearing transcripts.
 *
 * Uses shared hooks for selection, spotlight, and highlighting.
 * Keeps transcript-specific rendering: speaker colors, Q/A borders,
 * line numbers in gutter, page break markers.
 */

import React, {useMemo} from 'react';
import {DocumentRead, getTranscriptMetadata} from '../../../../schemas';
import {
    DocLine,
    HighlightOption,
    useLineSelection,
    useLineSpotlight,
    useLineHighlight,
    LineContainer,
    LineRow,
    renderHighlightedText,
    buildSpeakerMap,
    charOffsetToPageNumber,
    parseTranscriptLines,
} from './shared';
import type {TranscriptLine} from './shared';
export {isLikelyTranscript} from './shared';

// ── Convert to DocLine[] ─────────────────────────────────────────────────

function toDocLines(tLines: TranscriptLine[], pageBodyOffsets?: number[]): DocLine[] {
    return tLines.map((t, i) => ({
        lineIndex: i,
        text: t.content,
        lineType: t.isEmpty ? 'empty' as const : t.speaker ? 'speaker' as const : 'content' as const,
        indent: 0,
        metadata: {
            ...(t.lineNum != null ? {lineNum: String(t.lineNum)} : {}),
            ...(t.speaker ? {speaker: t.speaker, speakerRest: t.speakerRest} : {}),
            ...(t.isPageBreak ? {isPageBreak: 'true'} : {}),
        },
        pageNumber: charOffsetToPageNumber(t.bodyCharOffset, pageBodyOffsets),
    }));
}

// ── Transcript-specific line rendering ───────────────────────────────────

const TranscriptLineContent: React.FC<{
    tLine: TranscriptLine;
    highlights: {start: number; end: number; color: string}[];
    resolvedName?: string | null;
}> = ({tLine, highlights, resolvedName}) => {
    if (tLine.speaker) {
        const speakerRestHighlights = highlights
            .map(h => {
                const prefixLen = tLine.content.indexOf(tLine.speakerRest);
                if (prefixLen < 0) return null;
                const s = h.start - prefixLen;
                const e = h.end - prefixLen;
                if (e <= 0 || s >= tLine.speakerRest.length) return null;
                return {start: Math.max(0, s), end: Math.min(tLine.speakerRest.length, e), color: h.color};
            })
            .filter(Boolean) as {start: number; end: number; color: string}[];

        return (
            <>
                <span className="transcript-line__speaker-group">
                    <span
                        className="transcript-line__speaker"
                        title={tLine.speakerRaw || undefined}
                    >{tLine.speaker}.</span>
                    {resolvedName && (
                        <span className="transcript-line__resolved-name">{resolvedName}</span>
                    )}
                </span>
                {' '}
                {renderHighlightedText(tLine.speakerRest, speakerRestHighlights)}
            </>
        );
    }
    return <>{renderHighlightedText(tLine.content, highlights)}</>;
};

// ── Main component ───────────────────────────────────────────────────────

const TranscriptView: React.FC<{
    doc: DocumentRead;
    highlightOptions: HighlightOption[];
}> = ({doc, highlightOptions}) => {
    const text = doc.body || '';
    const tLines = useMemo(() => parseTranscriptLines(text), [text]);
    const docLines = useMemo(() => toDocLines(tLines, doc.page_body_offsets), [tLines, doc.page_body_offsets]);

    // Resolve Q/A speaker names from examination block metadata
    const transcriptMeta = useMemo(() => getTranscriptMetadata(doc), [doc]);
    const speakerMap = useMemo(
        () => buildSpeakerMap(tLines, transcriptMeta?.examination_index, transcriptMeta?.deponent_name ?? undefined),
        [tLines, transcriptMeta],
    );

    // Build filtered text for spotlight search — join the content of kept
    // lines so character-position → line-index mapping stays correct.
    const filteredText = useMemo(
        () => tLines.map(t => t.content).join('\n'),
        [tLines],
    );

    const selection = useLineSelection(docLines);
    const spotlight = useLineSpotlight(filteredText, tLines.length);
    const {getHighlightRanges} = useLineHighlight(highlightOptions);

    return (
        <LineContainer
            selection={selection}
            spotlight={spotlight}
            className="line-viewer--transcript"
        >
            {tLines.map((tLine, i) => {
                const highlights = getHighlightRanges(tLine.content);
                const names = speakerMap.get(i);
                const resolvedName = names
                    ? (tLine.speaker === 'Q' ? names.q : tLine.speaker === 'A' ? names.a : null)
                    : null;
                const curPage = docLines[i]?.pageNumber;
                const prevPage = i > 0 ? docLines[i - 1]?.pageNumber : undefined;
                const showDivider = curPage != null && prevPage != null && curPage !== prevPage;
                return (
                    <React.Fragment key={i}>
                        {showDivider && (
                            <div className="page-divider" data-page={curPage}>
                                <span className="page-divider__label">Page {curPage}</span>
                            </div>
                        )}
                        <LineRow
                            index={i}
                            text={tLine.content}
                            isSelected={selection.selectedLines.has(i)}
                            isSpotlighted={spotlight.spotlightedLines.has(i)}
                            spotlightClass={spotlight.spotlightClass}
                            highlights={highlights}
                            onClick={selection.onLineClick}
                            gutter={tLine.lineNum ?? ''}
                            isEmpty={tLine.isEmpty}
                            pageNumber={docLines[i]?.pageNumber}
                            bodyOffset={tLine.bodyCharOffset}
                            className={[
                                'transcript-line',
                                tLine.speaker ? 'transcript-line--speaker' : '',
                                tLine.speaker === 'Q' ? 'transcript-line--question' : '',
                                tLine.speaker === 'A' ? 'transcript-line--answer' : '',
                                tLine.isPageBreak ? 'transcript-line--page-break' : '',
                            ].filter(Boolean).join(' ')}
                            renderContent={(_, hl) => <TranscriptLineContent tLine={tLine} highlights={hl} resolvedName={resolvedName}/>}
                        />
                    </React.Fragment>
                );
            })}
        </LineContainer>
    );
};

export default TranscriptView;
